import React, { useState, useEffect } from 'react';
import QRCode from 'qrcode';

interface QRGeneratorProps {
  defaultTab?: 'url' | 'wifi' | 'whatsapp' | 'email' | 'vcard';
  hideTabs?: boolean;
}

export default function QRGenerator({ defaultTab = 'url', hideTabs = false }: QRGeneratorProps) {
  const [tab, setTab] = useState<'url' | 'wifi' | 'whatsapp' | 'email' | 'vcard'>(defaultTab);
  
  // URL state
  const [url, setUrl] = useState('https://getqrpost.com');
  
  // WiFi state
  const [ssid, setSsid] = useState('');
  const [password, setPassword] = useState('');
  const [security, setSecurity] = useState('WPA');
  
  // WhatsApp state
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  
  // Email state
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');

  // vCard state
  const [vcardName, setVcardName] = useState('');
  const [vcardOrg, setVcardOrg] = useState('');
  const [vcardTitle, setVcardTitle] = useState('');
  const [vcardPhone, setVcardPhone] = useState('');
  const [vcardEmail, setVcardEmail] = useState('');
  const [vcardUrl, setVcardUrl] = useState('');
  
  // Compiled QR string & generated image
  const [qrValue, setQrValue] = useState('https://getqrpost.com');
  const [qrImage, setQrImage] = useState('');

  // Design Customization States
  const [isGradient, setIsGradient] = useState<boolean>(true);
  const [fgColorStart, setFgColorStart] = useState('#3b82f6');
  const [fgColorEnd, setFgColorEnd] = useState('#6366f1');
  const [bgColor, setBgColor] = useState('#030712');
  const [logo, setLogo] = useState<string | null>(null);
  const [logoSize, setLogoSize] = useState<number>(18);
  const [shape, setShape] = useState<'square' | 'round'>('round');

  // Predefined Gradient Quick Buttons
  const gradientPresets = [
    { start: '#3b82f6', end: '#6366f1', name: 'Cool Sky' },
    { start: '#a855f7', end: '#ec4899', name: 'Fuchsia Glow' },
    { start: '#f43f5e', end: '#fb923c', name: 'Sunset Spark' },
    { start: '#10b981', end: '#3b82f6', name: 'Emerald Wave' },
  ];

  // Style Preset Templates
  const applyTemplate = (preset: 'classic' | 'professional' | 'social') => {
    if (preset === 'classic') {
      setFgColorStart('#000000');
      setFgColorEnd('#000000');
      setBgColor('#ffffff');
      setShape('square');
      setIsGradient(false);
    } else if (preset === 'professional') {
      setFgColorStart('#3b82f6');
      setFgColorEnd('#6366f1');
      setBgColor('#030712');
      setShape('round');
      setIsGradient(true);
    } else if (preset === 'social') {
      setFgColorStart('#a855f7');
      setFgColorEnd('#ec4899');
      setBgColor('#ffffff');
      setShape('round');
      setIsGradient(true);
    }
  };

  // Update compiled string based on active tab & inputs
  useEffect(() => {
    let value = '';
    if (tab === 'url') {
      value = url || 'https://getqrpost.com';
    } else if (tab === 'wifi') {
      value = `WIFI:S:${ssid};T:${security};P:${password};;`;
    } else if (tab === 'whatsapp') {
      const num = phone.replace(/[^0-9]/g, '');
      value = `https://wa.me/${num}${message ? `?text=${encodeURIComponent(message)}` : ''}`;
    } else if (tab === 'email') {
      value = `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    } else if (tab === 'vcard') {
      value = `BEGIN:VCARD\nVERSION:3.0\nFN:${vcardName}\nORG:${vcardOrg}\nTITLE:${vcardTitle}\nTEL;TYPE=CELL:${vcardPhone}\nEMAIL;TYPE=PREF,INTERNET:${vcardEmail}\nURL:${vcardUrl}\nEND:VCARD`;
    }
    setQrValue(value);
  }, [tab, url, ssid, password, security, phone, message, email, subject, body, vcardName, vcardOrg, vcardTitle, vcardPhone, vcardEmail, vcardUrl]);

  // Generate Custom QR Code canvas with dynamic dot shape styles, center logos, and gradients
  useEffect(() => {
    if (!qrValue) return;
    
    async function generateCustomQR() {
      try {
        const qr = QRCode.create(qrValue, { errorCorrectionLevel: 'H' });
        const modules = qr.modules;
        const count = modules.size;
        
        const size = 600;
        const margin = 40;
        const innerSize = size - margin * 2;
        const cellSize = innerSize / count;
        
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        
        // Draw background canvas
        ctx.fillStyle = bgColor;
        ctx.fillRect(0, 0, size, size);
        
        // Apply solid or linear gradient fill style
        if (isGradient) {
          const gradient = ctx.createLinearGradient(0, 0, size, size);
          gradient.addColorStop(0, fgColorStart);
          gradient.addColorStop(1, fgColorEnd);
          ctx.fillStyle = gradient;
        } else {
          ctx.fillStyle = fgColorStart;
        }
        
        // Draw customized dots
        for (let r = 0; r < count; r++) {
          for (let c = 0; c < count; c++) {
            const isDark = modules.get(r, c);
            if (isDark) {
              const x = margin + c * cellSize;
              const y = margin + r * cellSize;
              
              // Keep finder patterns sharp and square for scanning reliability
              const isFinder = 
                (r < 7 && c < 7) || 
                (r < 7 && c >= count - 7) || 
                (r >= count - 7 && c < 7);
              
              if (shape === 'round' && !isFinder) {
                ctx.beginPath();
                ctx.arc(x + cellSize / 2, y + cellSize / 2, (cellSize / 2) * 0.85, 0, 2 * Math.PI);
                ctx.fill();
              } else {
                ctx.fillRect(x, y, cellSize, cellSize);
              }
            }
          }
        }
        
        // Overlay center brand logo if uploaded
        if (logo) {
          const logoImg = new Image();
          logoImg.src = logo;
          await new Promise((resolve) => {
            logoImg.onload = resolve;
            logoImg.onerror = resolve;
          });
          
          if (logoImg.complete && logoImg.naturalWidth > 0) {
            const lSize = size * (logoSize / 100);
            const lx = (size - lSize) / 2;
            const ly = (size - lSize) / 2;
            
            // Draw matching background mask around the logo
            ctx.fillStyle = bgColor;
            ctx.beginPath();
            const borderRadius = lSize * 0.2;
            
            if (typeof ctx.roundRect === 'function') {
              ctx.roundRect(lx - 4, ly - 4, lSize + 8, lSize + 8, borderRadius);
            } else {
              ctx.rect(lx - 4, ly - 4, lSize + 8, lSize + 8);
            }
            ctx.fill();
            
            // Render the logo with clipping
            ctx.save();
            ctx.beginPath();
            if (typeof ctx.roundRect === 'function') {
              ctx.roundRect(lx, ly, lSize, lSize, borderRadius * 0.8);
            } else {
              ctx.rect(lx, ly, lSize, lSize);
            }
            ctx.clip();
            ctx.drawImage(logoImg, lx, ly, lSize, lSize);
            ctx.restore();
          }
        }
        
        setQrImage(canvas.toDataURL('image/png'));
      } catch (err) {
        console.error('Custom QR generation failed:', err);
      }
    }
    
    generateCustomQR();
  }, [qrValue, isGradient, fgColorStart, fgColorEnd, bgColor, logo, logoSize, shape]);

  const downloadPNG = () => {
    if (!qrImage) return;
    const link = document.createElement('a');
    link.href = qrImage;
    link.download = `getqrpost-${tab}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadSVG = () => {
    try {
      const qr = QRCode.create(qrValue, { errorCorrectionLevel: 'H' });
      const modules = qr.modules;
      const count = modules.size;
      
      let pathData = '';
      for (let r = 0; r < count; r++) {
        for (let c = 0; c < count; c++) {
          const isDark = modules.get(r, c);
          if (isDark) {
            const x = c;
            const y = r;
            
            const isFinder = 
              (r < 7 && c < 7) || 
              (r < 7 && c >= count - 7) || 
              (r >= count - 7 && c < 7);
              
            if (dotStyle === 'rounded' && !isFinder) {
              const radius = 0.42;
              const cx = x + 0.5;
              const cy = y + 0.5;
              pathData += `M ${cx} ${cy} m -${radius}, 0 a ${radius},${radius} 0 1,0 ${radius * 2},0 a ${radius},${radius} 0 1,0 -${radius * 2},0 `;
            } else {
              pathData += `M ${x} ${y} h 1 v 1 h -1 z `;
            }
          }
        }
      }
      
      let logoSvg = '';
      if (logo) {
        const lSize = count * (logoSize / 100);
        const lx = (count - lSize) / 2;
        const ly = (count - lSize) / 2;
        
        logoSvg = `
          <rect x="${lx - 0.2}" y="${ly - 0.2}" width="${lSize + 0.4}" height="${lSize + 0.4}" rx="${lSize * 0.1}" fill="${bgColor}" />
          <image href="${logo}" x="${lx}" y="${ly}" width="${lSize}" height="${lSize}" preserveAspectRatio="xMidYMid slice" clip-path="inset(0% round ${lSize * 0.08}px)" />
        `;
      }
      
      const svgString = `
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="-2 -2 ${count + 4} ${count + 4}" width="600" height="600">
          <rect x="-2" y="-2" width="${count + 4}" height="${count + 4}" fill="${bgColor}" />
          <defs>
            <linearGradient id="qr-grad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stop-color="${fgColorStart}" />
              <stop offset="100%" stop-color="${isGradient ? fgColorEnd : fgColorStart}" />
            </linearGradient>
          </defs>
          <path d="${pathData}" fill="url(#qr-grad)" />
          ${logoSvg}
        </svg>
      `;
      
      const blob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `getqrpost-${tab}.svg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Failed to export vector SVG:', err);
    }
  };

  return (
    <div className="w-full flex flex-col gap-6 max-w-4xl mx-auto">
      <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
        {/* Configuration Panel */}
        <div className="md:col-span-3 bg-gray-900/40 border border-gray-800 rounded-2xl p-6 flex flex-col gap-6 backdrop-blur-sm">
          {/* Category Tabs */}
          {!hideTabs && (
            <div className="flex gap-2 border-b border-gray-800 pb-4 overflow-x-auto font-sans">
              {(['url', 'wifi', 'whatsapp', 'email', 'vcard'] as const).map((type) => (
                <button
                  key={type}
                  onClick={() => setTab(type)}
                  className={`px-4 py-2 rounded-lg text-xs font-semibold uppercase tracking-wider transition-all duration-200 whitespace-nowrap ${
                    tab === type
                      ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-500/20'
                      : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800/40'
                  }`}
                >
                  {type === 'url' ? 'Link / URL' : type === 'wifi' ? 'Wi-Fi' : type === 'whatsapp' ? 'WhatsApp' : type === 'email' ? 'Email' : 'vCard / Contact'}
                </button>
              ))}
            </div>
          )}

          {/* Tab Inputs */}
          <div className="flex flex-col gap-4">
            {tab === 'url' && (
              <div className="flex flex-col gap-2">
                <label className="text-xs font-bold text-gray-400 uppercase tracking-wide">Enter Website Link</label>
                <input
                  type="text"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://example.com"
                  className="w-full bg-gray-950/80 border border-gray-800 focus:border-indigo-500 rounded-xl px-4 py-3 text-sm text-gray-100 placeholder-gray-600 outline-none transition-all duration-200"
                />
              </div>
            )}

            {tab === 'wifi' && (
              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-2">
                  <label className="text-xs font-bold text-gray-400 uppercase tracking-wide">Network SSID (Wi-Fi Name)</label>
                  <input
                    type="text"
                    value={ssid}
                    onChange={(e) => setSsid(e.target.value)}
                    placeholder="e.g. My Guest Network"
                    className="w-full bg-gray-950/80 border border-gray-800 focus:border-indigo-500 rounded-xl px-4 py-3 text-sm text-gray-100 placeholder-gray-600 outline-none transition-all duration-200"
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <label className="text-xs font-bold text-gray-400 uppercase tracking-wide">Network Password</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter password"
                    className="w-full bg-gray-950/80 border border-gray-800 focus:border-indigo-500 rounded-xl px-4 py-3 text-sm text-gray-100 placeholder-gray-600 outline-none transition-all duration-200"
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <label className="text-xs font-bold text-gray-400 uppercase tracking-wide">Security Type</label>
                  <select
                    value={security}
                    onChange={(e) => setSecurity(e.target.value)}
                    className="w-full bg-gray-950/80 border border-gray-800 focus:border-indigo-500 rounded-xl px-4 py-3 text-sm text-gray-100 outline-none transition-all duration-200"
                  >
                    <option value="WPA">WPA/WPA2 (Recommended)</option>
                    <option value="WEP">WEP (Older)</option>
                    <option value="nopass">Unsecured (No Password)</option>
                  </select>
                </div>
              </div>
            )}

            {tab === 'whatsapp' && (
              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-2">
                  <label className="text-xs font-bold text-gray-400 uppercase tracking-wide">WhatsApp Phone Number</label>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="e.g. +1234567890"
                    className="w-full bg-gray-950/80 border border-gray-800 focus:border-indigo-500 rounded-xl px-4 py-3 text-sm text-gray-100 placeholder-gray-600 outline-none transition-all duration-200"
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <label className="text-xs font-bold text-gray-400 uppercase tracking-wide">Default Message (Optional)</label>
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Hello! I would like to inquire about..."
                    rows={3}
                    className="w-full bg-gray-950/80 border border-gray-800 focus:border-indigo-500 rounded-xl px-4 py-3 text-sm text-gray-100 placeholder-gray-600 outline-none transition-all duration-200 resize-none"
                  />
                </div>
              </div>
            )}

            {tab === 'email' && (
              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-2">
                  <label className="text-xs font-bold text-gray-400 uppercase tracking-wide">Recipient Email Address</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="contact@example.com"
                    className="w-full bg-gray-950/80 border border-gray-800 focus:border-indigo-500 rounded-xl px-4 py-3 text-sm text-gray-100 placeholder-gray-600 outline-none transition-all duration-200"
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <label className="text-xs font-bold text-gray-400 uppercase tracking-wide">Subject Line</label>
                  <input
                    type="text"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder="Feedback or Inquiry"
                    className="w-full bg-gray-950/80 border border-gray-800 focus:border-indigo-500 rounded-xl px-4 py-3 text-sm text-gray-100 placeholder-gray-600 outline-none transition-all duration-200"
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <label className="text-xs font-bold text-gray-400 uppercase tracking-wide">Message Content</label>
                  <textarea
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    placeholder="Write details..."
                    rows={3}
                    className="w-full bg-gray-950/80 border border-gray-800 focus:border-indigo-500 rounded-xl px-4 py-3 text-sm text-gray-100 placeholder-gray-600 outline-none transition-all duration-200 resize-none"
                  />
                </div>
              </div>
            )}

            {tab === 'vcard' && (
              <div className="flex flex-col gap-4 font-sans text-left">
                <div className="flex flex-col gap-2">
                  <label className="text-xs font-bold text-gray-400 uppercase tracking-wide">Full Name</label>
                  <input
                    type="text"
                    value={vcardName}
                    onChange={(e) => setVcardName(e.target.value)}
                    placeholder="e.g. John Doe"
                    className="w-full bg-gray-950/80 border border-gray-800 focus:border-indigo-500 rounded-xl px-4 py-3 text-sm text-gray-100 placeholder-gray-600 outline-none transition-all duration-200"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex flex-col gap-2">
                    <label className="text-xs font-bold text-gray-400 uppercase tracking-wide">Organization / Company</label>
                    <input
                      type="text"
                      value={vcardOrg}
                      onChange={(e) => setVcardOrg(e.target.value)}
                      placeholder="e.g. Acme Corp"
                      className="w-full bg-gray-950/80 border border-gray-800 focus:border-indigo-500 rounded-xl px-4 py-3 text-sm text-gray-100 placeholder-gray-600 outline-none transition-all duration-200"
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="text-xs font-bold text-gray-400 uppercase tracking-wide">Job Title</label>
                    <input
                      type="text"
                      value={vcardTitle}
                      onChange={(e) => setVcardTitle(e.target.value)}
                      placeholder="e.g. Director of Sales"
                      className="w-full bg-gray-950/80 border border-gray-800 focus:border-indigo-500 rounded-xl px-4 py-3 text-sm text-gray-100 placeholder-gray-600 outline-none transition-all duration-200"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex flex-col gap-2">
                    <label className="text-xs font-bold text-gray-400 uppercase tracking-wide">Phone Number</label>
                    <input
                      type="tel"
                      value={vcardPhone}
                      onChange={(e) => setVcardPhone(e.target.value)}
                      placeholder="e.g. +1234567890"
                      className="w-full bg-gray-950/80 border border-gray-800 focus:border-indigo-500 rounded-xl px-4 py-3 text-sm text-gray-100 placeholder-gray-600 outline-none transition-all duration-200"
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="text-xs font-bold text-gray-400 uppercase tracking-wide">Email Address</label>
                    <input
                      type="email"
                      value={vcardEmail}
                      onChange={(e) => setVcardEmail(e.target.value)}
                      placeholder="e.g. john@example.com"
                      className="w-full bg-gray-950/80 border border-gray-800 focus:border-indigo-500 rounded-xl px-4 py-3 text-sm text-gray-100 placeholder-gray-600 outline-none transition-all duration-200"
                    />
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  <label className="text-xs font-bold text-gray-400 uppercase tracking-wide">Website URL</label>
                  <input
                    type="url"
                    value={vcardUrl}
                    onChange={(e) => setVcardUrl(e.target.value)}
                    placeholder="e.g. https://example.com"
                    className="w-full bg-gray-950/80 border border-gray-800 focus:border-indigo-500 rounded-xl px-4 py-3 text-sm text-gray-100 placeholder-gray-600 outline-none transition-all duration-200"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Design Customization Section */}
          <div className="border-t border-gray-800 pt-6 mt-2 flex flex-col gap-5">
            <h3 className="text-sm font-bold text-gray-200 tracking-wide uppercase flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-indigo-500"></span>
              Create Your QR Code
            </h3>
            
            {/* 1. Quick Style Preset Templates */}
            <div className="flex flex-col gap-2">
              <label className="text-xs font-bold text-gray-400 uppercase tracking-wide">Quick Style Presets</label>
              <div className="grid grid-cols-3 gap-2">
                <button
                  type="button"
                  onClick={() => applyTemplate('classic')}
                  className="py-2.5 px-3 rounded-xl border border-gray-800 bg-gray-950/40 text-xs font-bold hover:text-gray-200 hover:border-gray-700 transition-all text-center"
                >
                  Classic Black
                </button>
                <button
                  type="button"
                  onClick={() => applyTemplate('professional')}
                  className="py-2.5 px-3 rounded-xl border border-indigo-500/20 bg-indigo-950/10 text-xs font-bold text-indigo-400 hover:text-indigo-300 transition-all text-center"
                >
                  Pro Dark Gradient
                </button>
                <button
                  type="button"
                  onClick={() => applyTemplate('social')}
                  className="py-2.5 px-3 rounded-xl border border-purple-500/20 bg-purple-950/10 text-xs font-bold text-purple-400 hover:text-purple-300 transition-all text-center"
                >
                  Social Light Gradient
                </button>
              </div>
            </div>

            {/* 2. Colors & Gradients */}
            <div className="flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <label className="text-xs font-bold text-gray-400 uppercase tracking-wide">Change Colors</label>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-gray-400 font-semibold">Gradient Color</span>
                  <input
                    type="checkbox"
                    checked={isGradient}
                    onChange={(e) => setIsGradient(e.target.checked)}
                    className="h-4 w-4 bg-gray-950 border-gray-800 rounded focus:ring-0 cursor-pointer accent-indigo-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex flex-col gap-2">
                  <span className="text-[10px] font-bold text-gray-500 uppercase">Primary Color</span>
                  <div className="flex gap-2 items-center">
                    <input
                      type="color"
                      value={fgColorStart}
                      onChange={(e) => setFgColorStart(e.target.value)}
                      className="h-9 w-9 bg-transparent border-0 rounded-lg cursor-pointer outline-none"
                    />
                    <input
                      type="text"
                      value={fgColorStart}
                      onChange={(e) => setFgColorStart(e.target.value)}
                      className="flex-1 bg-gray-950/80 border border-gray-800 focus:border-indigo-500 rounded-xl px-3 py-1.5 text-xs text-gray-100 placeholder-gray-600 outline-none transition-all"
                    />
                  </div>
                </div>

                {isGradient && (
                  <div className="flex flex-col gap-2">
                    <span className="text-[10px] font-bold text-gray-500 uppercase">Secondary Color</span>
                    <div className="flex gap-2 items-center">
                      <input
                        type="color"
                        value={fgColorEnd}
                        onChange={(e) => setFgColorEnd(e.target.value)}
                        className="h-9 w-9 bg-transparent border-0 rounded-lg cursor-pointer outline-none"
                      />
                      <input
                        type="text"
                        value={fgColorEnd}
                        onChange={(e) => setFgColorEnd(e.target.value)}
                        className="flex-1 bg-gray-950/80 border border-gray-800 focus:border-indigo-500 rounded-xl px-3 py-1.5 text-xs text-gray-100 placeholder-gray-600 outline-none transition-all"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Predefined Gradient Presets Row */}
              {isGradient && (
                <div className="flex items-center gap-1.5 overflow-x-auto py-1">
                  {gradientPresets.map((preset, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => {
                        setFgColorStart(preset.start);
                        setFgColorEnd(preset.end);
                      }}
                      className="px-2.5 py-1.5 rounded-lg text-[10px] font-semibold bg-gray-950/60 border border-gray-800/80 flex items-center gap-1.5 hover:border-gray-700 hover:text-gray-200 transition-all select-none"
                    >
                      <span 
                        className="h-3 w-3 rounded-full border border-white/10" 
                        style={{ background: `linear-gradient(135deg, ${preset.start}, ${preset.end})` }}
                      ></span>
                      {preset.name}
                    </button>
                  ))}
                </div>
              )}

              <div className="flex flex-col gap-2 mt-1">
                <span className="text-[10px] font-bold text-gray-500 uppercase">Background Color</span>
                <div className="flex gap-2 items-center">
                  <input
                    type="color"
                    value={bgColor}
                    onChange={(e) => setBgColor(e.target.value)}
                    className="h-9 w-9 bg-transparent border-0 rounded-lg cursor-pointer outline-none"
                  />
                  <input
                    type="text"
                    value={bgColor}
                    onChange={(e) => setBgColor(e.target.value)}
                    className="flex-1 bg-gray-950/80 border border-gray-800 focus:border-indigo-500 rounded-xl px-3 py-1.5 text-xs text-gray-100 placeholder-gray-600 outline-none transition-all"
                  />
                </div>
              </div>
            </div>

            {/* 3. Shape Selector */}
            <div className="flex flex-col gap-2 font-sans text-left">
              <label className="text-xs font-bold text-gray-400 uppercase tracking-wide">Shape</label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setShape('square')}
                  className={`py-2 px-3 rounded-xl border text-xs font-semibold tracking-wider transition-all duration-200 ${
                    shape === 'square'
                      ? 'bg-indigo-600/10 text-indigo-400 border-indigo-500/40 font-bold'
                      : 'bg-gray-950/40 text-gray-400 border-gray-800 hover:text-gray-200'
                  }`}
                >
                  Square
                </button>
                <button
                  type="button"
                  onClick={() => setShape('round')}
                  className={`py-2 px-3 rounded-xl border text-xs font-semibold tracking-wider transition-all duration-200 ${
                    shape === 'round'
                      ? 'bg-indigo-600/10 text-indigo-400 border-indigo-500/40 font-bold'
                      : 'bg-gray-950/40 text-gray-400 border-gray-800 hover:text-gray-200'
                  }`}
                >
                  Round
                </button>
              </div>
            </div>

            {/* 4. Logo Upload Zone */}
            <div className="flex flex-col gap-2">
              <label className="text-xs font-bold text-gray-400 uppercase tracking-wide">Add Your Logo</label>
              {logo ? (
                <div className="flex items-center gap-3 bg-gray-950/80 border border-gray-800 rounded-xl p-2 justify-between">
                  <div className="flex items-center gap-2">
                    <img src={logo} alt="Custom Logo" className="h-8 w-8 object-contain rounded bg-gray-900 border border-gray-800" />
                    <span className="text-[10px] text-gray-400 font-medium">Custom Logo Active</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setLogo(null)}
                    className="px-2 py-1 bg-red-950/40 border border-red-900/30 hover:border-red-950 text-red-400 hover:text-red-300 text-[10px] uppercase font-bold rounded-lg transition-all"
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <label className="flex items-center justify-center border border-dashed border-gray-800 hover:border-indigo-500/60 rounded-xl p-4 cursor-pointer bg-gray-950/40 hover:bg-indigo-500/5 transition-all duration-200">
                  <span className="text-xs text-gray-500 hover:text-indigo-400 font-semibold flex flex-col items-center gap-1.5">
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                    </svg>
                    Upload Brand Logo
                  </span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        const reader = new FileReader();
                        reader.onload = (event) => {
                          if (event.target?.result) {
                            setLogo(event.target.result as string);
                          }
                        };
                        reader.readAsDataURL(file);
                      }
                    }}
                    className="hidden"
                  />
                </label>
              )}
            </div>

            {logo && (
              <div className="flex flex-col gap-2 pt-2 border-t border-gray-900/60">
                <div className="flex justify-between text-xs font-bold text-gray-400 uppercase tracking-wide">
                  <span>Logo Size</span>
                  <span className="text-indigo-400">{logoSize}%</span>
                </div>
                <input
                  type="range"
                  min="10"
                  max="25"
                  value={logoSize}
                  onChange={(e) => setLogoSize(parseInt(e.target.value))}
                  className="w-full h-1.5 bg-gray-950 rounded-lg appearance-none cursor-pointer accent-indigo-500"
                />
              </div>
            )}
          </div>
        </div>

        {/* Real-time Render & Download Panel */}
        <div className="md:col-span-2 flex flex-col items-center justify-between bg-gray-900/20 border border-gray-800 rounded-2xl p-6 backdrop-blur-sm shadow-xl" style={{ borderColor: fgColorStart + '30' }}>
          <div className="flex flex-col items-center gap-4 w-full">
            <h3 className="text-sm font-bold text-gray-300 tracking-wide uppercase">Your QR Code</h3>
            
            <div className="h-48 w-48 rounded-xl border flex items-center justify-center p-3 shadow-inner hover:scale-[1.03] transition-all duration-300" style={{ backgroundColor: bgColor, borderColor: fgColorStart + '20' }}>
              {qrImage ? (
                <img src={qrImage} alt="Compiled QR Code" className="h-full w-full object-contain rounded-md animate-in fade-in zoom-in-95 duration-200" />
              ) : (
                <div className="text-gray-700 text-xs font-semibold uppercase animate-pulse">Rendering...</div>
              )}
            </div>
            
            <div className="text-center flex flex-col gap-1">
              <span className="text-[10px] uppercase font-bold text-emerald-400 tracking-wider flex items-center gap-1.5 justify-center">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                Secure Client Compilation
              </span>
              <span className="text-[10px] text-gray-500 font-medium">
                Scan this with your phone camera to test it!
              </span>
            </div>
          </div>

          {/* Double Download Buttons for PNG and High-Quality Vector SVG */}
          <div className="w-full mt-6 flex flex-col gap-2">
            <button
              onClick={downloadPNG}
              disabled={!qrImage}
              className="w-full py-3 px-4 rounded-xl bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white font-bold text-sm shadow-lg shadow-indigo-500/20 hover:shadow-indigo-500/35 transition-all duration-200 active:scale-[0.98] disabled:opacity-40"
            >
              Download (Standard)
            </button>
            <button
              onClick={downloadSVG}
              disabled={!qrImage}
              className="w-full py-2.5 px-4 rounded-xl bg-gray-900 hover:bg-gray-800 border border-gray-800 text-gray-200 font-bold text-xs shadow hover:border-indigo-500/35 transition-all duration-200 active:scale-[0.98] disabled:opacity-40"
            >
              Download for Print (High Quality)
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
